import { BrowserAuthError, BrowserAuthErrorCodes, EventType, PublicClientApplication } from '@azure/msal-browser';
import { InteractionRequiredAuthError } from '@azure/msal-browser';

import * as Config from './EnviromentConfig';
import {
  getIsNativeContext,
  getNativeTokens,
  isNativeTokenValid,
  isNativeWebview,
  refreshViaNativeBridge,
  waitForNativeTokens,
} from './nativeTokenStore';

import type {
  AccountInfo,
  AuthenticationResult,
  Configuration,
  EventMessage,
  RedirectRequest,
} from '@azure/msal-browser';

export const msalSettings = {
  clientId: Config.CLIENT_ID,
  authorities: {
    signUpSignIn: Config.SIGN_UP_SIGN_IN_URI,
    editProfile: Config.EDIT_PROFILE_URI,
  },
  domain: Config.DOMAIN_URI,
};

export const msalConfig: Configuration = {
  auth: {
    clientId: msalSettings.clientId,
    authority: msalSettings.authorities.signUpSignIn,
    knownAuthorities: [msalSettings.domain],
    redirectUri: Config.REDIRECT_URI,
    postLogoutRedirectUri: Config.POST_LOGOUT_URI,
  },
  cache: {
    cacheLocation: 'localStorage',
  },
};

export const loginRequest: RedirectRequest = {
  scopes: ['openid', 'profile'],
};

/**
 * In-memory auth cache for mobile webview fallback.
 * Stores auth data in JS memory when MSAL localStorage may fail on iOS/Android.
 */
export interface AuthCache {
  idToken: string;
  account: AccountInfo;
  timestamp: string;
  source: 'ACQUIRE_TOKEN_SUCCESS';
}
let authCache: AuthCache | null = null;

// V5: Use synchronous constructor, call initialize() in main.tsx
export const msalInstance = new PublicClientApplication(msalConfig);

// Event callback for token acquisition and login events
msalInstance.addEventCallback((event: EventMessage) => {
  if (event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS && event.payload) {
    const payload = event.payload as AuthenticationResult;

    // Store in memory cache for mobile webview fallback
    authCache = {
      idToken: payload.idToken,
      account: payload.account,
      timestamp: new Date().toISOString(),
      source: 'ACQUIRE_TOKEN_SUCCESS',
    };
  }

  if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
    // In MSAL v5, LOGIN_SUCCESS payload is AccountInfo (not AuthenticationResult).
    // ACQUIRE_TOKEN_SUCCESS fires immediately after and provides the full token result.
    const account = event.payload as AccountInfo;
    msalInstance.setActiveAccount(account);
  }

  if (event.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
    console.warn('Login failed', event.error);

    // Cancelling the edit-profile flow must not sign the user out.
    if (isProfileEditCancelled(event.error)) {
      return;
    }

    if (!isNativeWebview()) {
      msalInstance.setActiveAccount(null);
      msalInstance.logoutRedirect();
    }
  }
});

// B2C returns AADB2C90091 when the user cancels the edit-profile policy.
export const isProfileEditCancelled = (error: unknown): boolean => {
  const err = error as { errorCode?: string; errorMessage?: string; message?: string } | null;
  const text = `${err?.errorMessage ?? ''} ${err?.message ?? ''}`;
  return err?.errorCode === 'access_denied' && text.includes('AADB2C90091');
};

// Token acquisition queue to prevent race conditions
let tokenAcquisitionPromise: Promise<string> | null = null;

export const getToken = async () => {
  // If there's already a token acquisition in progress, wait for it
  if (tokenAcquisitionPromise) {
    return tokenAcquisitionPromise;
  }

  // Create a new token acquisition promise
  tokenAcquisitionPromise = (async () => {
    try {
      if (isNativeWebview() || getIsNativeContext()) {
        // --- NATIVE WEBVIEW: tokens come exclusively from native MSAL ---
        // The webview has no MSAL session of its own; native handles all auth.
        if (isNativeTokenValid()) {
          return getNativeTokens()!.accessToken;
        }

        // If handshake completed, try refreshing via native bridge
        if (getIsNativeContext()) {
          return await refreshViaNativeBridge();
        }

        // Handshake not yet completed — wait for tokens to arrive
        return await waitForNativeTokens();
      }

      // --- BROWSER: standard MSAL flow ---
      const account = msalInstance.getActiveAccount();

      if (!account) {
        throw new Error('No active account! Please login.');
      }

      // acquireTokenSilent synchronously emits MSAL events (picked up by MsalProvider)
      // before its first await. When getToken() is invoked from a TanStack Query
      // fetchFn started during render (e.g. useSuspenseQuery), that synchronous
      // dispatch updates MsalProvider while AuthenticatedApp is still rendering,
      // triggering React's "Cannot update a component while rendering a different
      // component" warning. Deferring to a microtask moves the call outside of
      // React's render call stack.
      const { accessToken } = await new Promise<AuthenticationResult>((resolve, reject) => {
        queueMicrotask(() => {
          msalInstance
            .acquireTokenSilent({
              account: account,
              scopes: Config.ECM_SCOPES,
              // Use a blank page as the silent-renew redirect URI so the iframe does not
              // bootstrap the full React app (which would re-mount useMsalAuthentication
              // and conflict with the ongoing interaction, causing timed_out in MSAL v5).
              redirectUri: `${window.location.origin}${import.meta.env.BASE_URL ?? '/'}blank.html`,
            })
            .then(resolve, reject);
        });
      });

      return accessToken;
    } catch (err) {
      console.error('[getToken] Token acquisition failed', err);

      const isInteractionRequired = err instanceof InteractionRequiredAuthError;
      const isTimedOut = err instanceof BrowserAuthError && err.errorCode === BrowserAuthErrorCodes.timedOut;

      if ((isInteractionRequired || isTimedOut) && !isNativeWebview() && !getIsNativeContext()) {
        await msalInstance.acquireTokenRedirect({
          scopes: Config.ECM_SCOPES,
        });
      }

      throw err;
    } finally {
      tokenAcquisitionPromise = null;
    }
  })();

  return tokenAcquisitionPromise;
};

/**
 * Get the in-memory auth cache for mobile webview fallback.
 * Returns null if no auth event has occurred yet.
 */
export const getAuthCache = (): AuthCache | null => authCache;

export default msalInstance;
